import { getSpecsConfigJsonFromEnvironment } from "@/utils/teamService";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";

const TRUTHY_ENV_VAR_VALUES = new Set(["true", "1", "yes", "t", "y", "on"]);

const isTruthyEnvVarValue = (val: string | number) => {
  return TRUTHY_ENV_VAR_VALUES.has(String(val).toLowerCase());
};

export const DANGEROUS_ENV_VAR_VALUES: Record<
  string,
  { fn: (val: string | number) => boolean; description: string }
> = {
  CHALK_REMOTE_INVOKER_SEQUENTIAL_WARMUP: {
    fn: (val) => String(val) === "1",
    description:
      "Causes the remote invoker actor pool to be warmed up sequentially, may cause unexpected timeouts",
  },
  CHALK_SUBPROCESS_ENTRYPOINT: {
    fn: (_val) => true,
    description:
      "Changes the command used to start subprocesses, could result in failed startup or different behavior.",
  },
  CHALK_BIGQUERY_USE_LEGACY_FIELD_MAPPING_FOR_SOCURE_RISKOS: {
    fn: isTruthyEnvVarValue,
    description:
      "Single-customer escape hatch that reverts BigQuery SQL resolvers with postprocessing to pre-#39997 field mapping behavior, silently tolerating incorrect resolver `fields` mappings. No one should turn this on; fix the resolver's `fields` mapping instead.",
  },
  CHALK_MEMRAY_MODE: {
    fn: (val) => String(val) === "file" || String(val) === "socket",
    description: "Intrusively captures all allocations, significantly affecting performance.",
  },
  CHALK_QUERY_LOGGING: {
    fn: isTruthyEnvVarValue,
    description:
      "Logs query execution at info level, dramatically increasing log volume. Turn off when possible.",
  },
  CHALK_SKIP_RESULT_PERSISTENCE: {
    fn: (val) => String(val) === "1",
    description: "Prevents feature values from being saved to the online and offline stores.",
  },
  CHALK_SKIP_GIVENS_PERSISTENCE_ONLINE_QUERY: {
    fn: (val) => String(val) === "1",
    description: "Prevents givens from being persisted during online query",
  },
  CHALK_SKIP_METRICS_PERSISTENCE: {
    fn: (val) => String(val) === "1",
    description: "Prevents metrics from being saved when running queries.",
  },
  CHALK_SKIP_OFFLINE_QUERY_RESULT_PERSISTENCE: {
    fn: (val) => String(val) === "1",
    description: "Prevents offline query results from being saved.",
  },
  CHALK_SKIP_PG_DATETIME_ZONE_CAST: {
    fn: (val) => String(val) === "1",
    description: "Disables timezone casting for datetime values in Postgres-compatible databases.",
  },
  CHALK_SKIP_PLAN_PERSISTENCE: {
    fn: (val) => String(val) === "1",
    description: "Prevents query plans from being saved after planning.",
  },
  CHALK_SKIP_QUERY_PERSISTENCE: {
    fn: (val) => String(val) === "1",
    description: "Prevents queries from being saved to the queries table after execution.",
  },
};

export interface DangerousEnvVarDescription {
  name: string;
  value: string;
  description: string;
  service?: string;
  resourceGroup?: string;
}

export interface DangerousEnvVarsResult {
  hasDangerousEnvVars: boolean;
  dangerousEnvVars: DangerousEnvVarDescription[];
}

export function useHasDangerousEnvVars(): DangerousEnvVarsResult {
  const isChalkEmployee = useDANGEROUSisChalkEmployee({ ignoreAdminMode: true });
  const { environment } = useRouteAndTeamInfo();
  if (!isChalkEmployee) {
    return { hasDangerousEnvVars: false, dangerousEnvVars: [] };
  }
  if (!environment) {
    return { hasDangerousEnvVars: false, dangerousEnvVars: [] };
  }

  const dangerousEnvVars: DangerousEnvVarDescription[] = [];

  // Check additionalEnvVars (environment-level)
  const envVars = environment.additionalEnvVars;
  for (const [envVarName, dangerousValueInfo] of Object.entries(DANGEROUS_ENV_VAR_VALUES)) {
    const actualValue = envVars[envVarName];
    if (!actualValue) {
      continue;
    }

    if (dangerousValueInfo.fn(actualValue)) {
      dangerousEnvVars.push({
        name: envVarName,
        value: actualValue,
        description: dangerousValueInfo.description,
      });
    }
  }

  // Check env_overrides in specs config
  const specsConfig = getSpecsConfigJsonFromEnvironment(environment);
  if (specsConfig) {
    // Check top-level services (use "default" as resource group name)
    if (specsConfig.services) {
      for (const [serviceKind, service] of Object.entries(specsConfig.services)) {
        if (service.env_overrides) {
          for (const [envVarName, dangerousValueInfo] of Object.entries(DANGEROUS_ENV_VAR_VALUES)) {
            const actualValue = service.env_overrides[envVarName];
            if (!actualValue) {
              continue;
            }

            if (dangerousValueInfo.fn(actualValue)) {
              dangerousEnvVars.push({
                name: envVarName,
                value: String(actualValue),
                description: dangerousValueInfo.description,
                service: serviceKind,
                resourceGroup: "default",
              });
            }
          }
        }
      }
    }

    // Check services in resource groups
    if (specsConfig.resource_groups) {
      for (const group of specsConfig.resource_groups) {
        if (group.services) {
          for (const [serviceKind, service] of Object.entries(group.services)) {
            if (service.env_overrides) {
              for (const [envVarName, dangerousValueInfo] of Object.entries(
                DANGEROUS_ENV_VAR_VALUES,
              )) {
                const actualValue = service.env_overrides[envVarName];
                if (!actualValue) {
                  continue;
                }

                if (dangerousValueInfo.fn(actualValue)) {
                  dangerousEnvVars.push({
                    name: envVarName,
                    value: String(actualValue),
                    description: dangerousValueInfo.description,
                    service: serviceKind,
                    resourceGroup: group.resource_group_name || "unnamed",
                  });
                }
              }
            }
          }
        }
      }
    }
  }

  return {
    hasDangerousEnvVars: dangerousEnvVars.length > 0,
    dangerousEnvVars,
  };
}
